import React, { useState, useEffect } from "react";
import {
  Table,
  Spinner,
  Input,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
} from "reactstrap";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  FaHeart,
  FaRegHeart,
  FaEye,
  FaEdit,
  FaTrash,
  FaEllipsisV,
} from "react-icons/fa";
import { MdDriveFileRenameOutline } from "react-icons/md";
import { CSVLink } from "react-csv";
import { BiSolidFileExport } from "react-icons/bi";
import { MdAddCircleOutline } from "react-icons/md";
import Pagination from "../../../Components/Paginate";
import { toast } from "react-toastify";

const ListedPlacesTab = ({ partnerId }) => {
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [paginate, setPaginate] = useState(10);
  const [placeCount, setPlaceCount] = useState(0);
  const [checkedItems, setCheckedItems] = useState({});
  const [lovedItems, setLovedItems] = useState({});
  const [statusItems, setStatusItems] = useState({});
  const [actionDropdowns, setActionDropdowns] = useState({});
  const [renameModal, setRenameModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [newPlaceName, setNewPlaceName] = useState("");
  const {
    AuthReducer: { auth },
  } = useSelector((state) => state);

  useEffect(() => {
    if (partnerId) {
      fetchPartnerPlaces();
    }
  }, [partnerId, page, paginate, searchTerm]);

  const fetchPartnerPlaces = async () => {
    try {
      const response = await axios.get(
        `/api/admin/v1/partner-place-list/${partnerId}`,
        {
          headers: { Authorization: `Bearer ${auth?.token}` },
          params: {
            page: page + 1,
            limit: paginate,
            search: searchTerm,
          },
        }
      );
      setPlaces(response.data.data || []);
      setPlaceCount(response.data.count || 0);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (places && places.length > 0) {
      const initialCheckedItems = places.reduce((acc, item) => {
        acc[item.id] = item.booking_status === 1;
        return acc;
      }, {});
      setCheckedItems(initialCheckedItems);

      const initialLovedItems = places.reduce((acc, item) => {
        acc[item.id] = item.loved_place === 1;
        return acc;
      }, {});
      setLovedItems(initialLovedItems);

      const initialStatusItems = places.reduce((acc, item) => {
        acc[item.id] = Number(item.status) === 1;
        return acc;
      }, {});
      setStatusItems(initialStatusItems);
    }
  }, [places]);

  const handleCheckboxChange = async (id, checked) => {
    setCheckedItems((prev) => ({
      ...prev,
      [id]: checked,
    }));

    try {
      await axios.put(
        "/api/admin/v1/update-book-status",
        { id, status: checked ? 1 : 0 },
        { headers: { Authorization: `Bearer ${auth?.token}` } }
      );
    } catch (error) {}
  };

  const handleHeartClick = async (id) => {
    const newStatus = !lovedItems[id];
    setLovedItems((prev) => ({
      ...prev,
      [id]: newStatus,
    }));

    try {
      await axios.put(
        "/api/admin/v1/update-loved-place",
        { id, status: newStatus ? 1 : 0 },
        { headers: { Authorization: `Bearer ${auth?.token}` } }
      );
    } catch (error) {}
  };

  const handleStatusToggle = async (id) => {
    const newStatus = !statusItems[id];

    try {
      const response = await axios.put(
        `/api/admin/v1/update-place-status/${id}`,
        { status: newStatus ? 1 : 0 },
        { headers: { Authorization: `Bearer ${auth?.token}` } }
      );
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message || "Failed to update status");
      }
      setStatusItems((prev) => ({
        ...prev,
        [id]: Number(response.data.status) === 1,
      }));
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Error updating place status"
      );
    }
  };

  const handleRenamePlace = (place) => {
    setSelectedPlace(place);
    setNewPlaceName(place.place_name);
    setRenameModal(true);
  };

  const handleDeletePlace = (place) => {
    setSelectedPlace(place);
    setDeleteModal(true);
  };

  const confirmRename = async () => {
    try {
      await axios.put(
        `/api/admin/v1/update-place-name/${selectedPlace.id}`,
        { place_name: newPlaceName },
        { headers: { Authorization: `Bearer ${auth?.token}` } }
      );
      toast.success("Place renamed successfully");
      setRenameModal(false);
      fetchPartnerPlaces();
    } catch (error) {
      toast.error("Failed to rename place");
    }
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`/api/admin/v1/delete-place/${selectedPlace.id}`, {
        headers: { Authorization: `Bearer ${auth?.token}` },
      });
      toast.success("Place deleted successfully");
      setDeleteModal(false);
      fetchPartnerPlaces();
    } catch (error) {
      toast.error("Failed to delete place");
    }
  };

  const csvData = places.map((place, index) => ({
    "SL. No.": index + 1,
    Category:
      place?.place_category_ids
        ?.map((item) => item?.category?.name)
        .join(",") || "",
    "Sub Category":
      place?.place_sub_category_ids
        ?.map((item) => item?.sub_category?.name_english)
        .join(",") || "",
    "Sub Sub Category":
      place?.place_sub_sub_category_ids
        ?.map((item) => item?.sub_sub_category?.name_english)
        .join(",") || "",
    State: place?.state?.state_name || "",
    City: place?.city?.city_name || "",
    Name: place?.place_name || "",
    Status: place.status === 1 ? "Active" : "Inactive",
  }));

  if (loading) return <Spinner />;

  return (
    <div
      className="bg-white px-3 py-4 rounded-2"
      style={{ boxShadow: "4px 4px 18px rgba(0,0,0,0.15)" }}
    >
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h6 className="fw-bold mb-0 text-dark">Listed Places ({placeCount})</h6>
        <div className="d-flex gap-2">
          <CSVLink
            filename={`partner-${partnerId}-places.csv`}
            className="btn btn-outline-primary btn-sm"
            data={csvData}
          >
            <BiSolidFileExport className="me-1" />
            Export CSV
          </CSVLink>
          <a
            href={`/add-place?partnerId=${partnerId}`}
            className="btn btn-outline-primary btn-sm"
          >
            <MdAddCircleOutline className="me-1" />
            Add Place
          </a>
        </div>
      </div>

      <div className="row mb-3">
        <div className="col-md-3">
          <Input
            size="sm"
            value={searchTerm}
            placeholder="Search By Name"
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="col-md-2">
          <Input
            size="sm"
            type="select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </Input>
        </div>
      </div>

      <Table hover responsive>
        <thead>
          <tr>
            <th>SL. No.</th>
            <th>Image</th>
            <th>Name</th>
            <th>Category</th>
            <th>Sub Category</th>
            <th>State</th>
            <th>City</th>
            <th>Book</th>
            <th>Wishlist</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {places.length === 0 ? (
            <tr>
              <td colSpan="11" className="text-center">
                No places found
              </td>
            </tr>
          ) : (
            places.map((place, index) => (
              <tr key={place.id}>
                <td>{index + 1 + page * paginate}</td>
                <td>
                  <img
                    src={
                      process.env.REACT_APP_IMAGE_URL + `/${place?.place_image}`
                    }
                    height={45}
                    width={45}
                    alt={place?.place_name}
                    style={{ objectFit: "cover", borderRadius: "4px" }}
                  />
                </td>
                <td>{place?.place_name}</td>
                <td>
                  {place?.place_category_ids
                    ?.map((item) => item?.category?.name)
                    .join(",") || "N/A"}
                </td>
                <td>
                  {place?.place_sub_category_ids
                    ?.map((item) => item?.sub_category?.name_english)
                    .join(",") || "N/A"}
                </td>
                <td>{place?.state?.state_name || "N/A"}</td>
                <td>{place?.city?.city_name || "N/A"}</td>
                <td>
                  <input
                    type="checkbox"
                    checked={checkedItems[place.id] || false}
                    onChange={(e) =>
                      handleCheckboxChange(place.id, e.target.checked)
                    }
                  />
                </td>
                <td>
                  {lovedItems[place.id] ? (
                    <FaHeart
                      color="red"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleHeartClick(place.id)}
                    />
                  ) : (
                    <FaRegHeart
                      color="gray"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleHeartClick(place.id)}
                    />
                  )}
                </td>
                <td>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={statusItems[place.id] || false}
                      onChange={() => handleStatusToggle(place.id)}
                      style={{ cursor: "pointer" }}
                    />
                  </div>
                </td>
                <td className="text-white">
                  <Dropdown
                    isOpen={actionDropdowns[place.id] || false}
                    toggle={() =>
                      setActionDropdowns((prev) => ({
                        ...prev,
                        [place.id]: !prev[place.id],
                      }))
                    }
                  >
                    <DropdownToggle className="btn btn-sm btn-outline-secondary">
                      <FaEllipsisV className="text-white" />
                    </DropdownToggle>

                    <DropdownMenu>
                      <DropdownItem
                        onClick={() =>
                          window.open(`/place-details/${place.id}`, "_blank")
                        }
                      >
                        <FaEye className="me-2" /> View Details
                      </DropdownItem>
                      <DropdownItem
                        onClick={() =>
                          window.open(`/edit-place/${place.id}`, "_blank")
                        }
                      >
                        <FaEdit className="me-2" /> Edit Place
                      </DropdownItem>
                      <DropdownItem onClick={() => handleRenamePlace(place)}>
                        <MdDriveFileRenameOutline className="me-2" /> Rename
                        Place
                      </DropdownItem>
                      <DropdownItem
                        onClick={() => handleDeletePlace(place)}
                        className="text-danger"
                      >
                        <FaTrash className="me-2" /> Delete Place
                      </DropdownItem>
                    </DropdownMenu>
                  </Dropdown>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      <div className="row align-items-center mt-3">
        <div className="col-md-6 d-flex">
          <div className="col-md-3">
            <Input
              type="select"
              value={paginate}
              onChange={(e) => setPaginate(parseInt(e.target.value))}
              size="sm"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </Input>
          </div>
        </div>
        <div className="col-md-6">
          <Pagination
            totalItems={placeCount}
            itemsPerPage={paginate}
            currentPage={page}
            onPageChange={setPage}
          />
        </div>
      </div>

      {/* Rename Modal */}
      <Modal isOpen={renameModal} toggle={() => setRenameModal(false)}>
        <ModalHeader toggle={() => setRenameModal(false)}>
          Rename Place
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>New Place Name</Label>
            <Input
              type="text"
              value={newPlaceName}
              onChange={(e) => setNewPlaceName(e.target.value)}
              placeholder="Enter new place name"
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setRenameModal(false)}>
            Cancel
          </Button>
          <Button color="primary" onClick={confirmRename}>
            Rename
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Modal */}
      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)}>
        <ModalHeader toggle={() => setDeleteModal(false)}>
          Delete Place
        </ModalHeader>
        <ModalBody>
          <p>
            Are you sure you want to delete{" "}
            <strong>{selectedPlace?.place_name}</strong>?
          </p>
          <p className="text-danger">This action cannot be undone.</p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setDeleteModal(false)}>
            Cancel
          </Button>
          <Button color="danger" onClick={confirmDelete}>
            Delete
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ListedPlacesTab;
